import { useState } from 'react';
import { Form, Input, Select, Button, message, Tabs, Collapse } from 'antd';
import ReactJson from '@microlink/react-json-view';
import { testApi } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const sectionStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: '#1e293b',
  marginBottom: 12,
  paddingBottom: 6,
  borderBottom: '1px solid #f0f0f0',
};
const sectionStyleMt = { ...sectionStyle, marginTop: 8 };
const gridCol2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 };

const CHANNEL_OPTIONS = [
  { label: 'BANKING_CIRCLE', value: 'BANKING_CIRCLE' },
  { label: 'SGB', value: 'SGB' },
];

const CHARGE_TYPE_OPTIONS = [
  { label: 'BEN', value: 'BEN' },
  { label: 'OUR', value: 'OUR' },
  { label: 'SHA', value: 'SHA' },
];

const renderResultPanel = (result, emptyText) => {
  if (result) {
    return (
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0', padding: 20, minHeight: 300 }}>
        <ReactJson src={result} collapsed={1} theme="rjv-default" displayDataTypes={false} displayObjectSize={false} enableClipboard />
      </div>
    );
  }
  return (
    <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0', padding: 20, minHeight: 300 }}>
      <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 8, border: '2px dashed #e5e7eb' }}>
        <div style={{ marginTop: 12 }}>{emptyText || '提交后显示结果'}</div>
      </div>
    </div>
  );
};

export default function FundTransfer() {
  const { t } = useLanguage();
  const [createForm] = Form.useForm();
  const [queryForm] = Form.useForm();
  const [txnForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [queryLoading, setQueryLoading] = useState(false);
  const [txnLoading, setTxnLoading] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [queryResult, setQueryResult] = useState(null);
  const [txnResult, setTxnResult] = useState(null);

  // --- Tab 1: Create Fund Transfer ---
  const handleCreate = async (values) => {
    setLoading(true);
    try {
      // Build extParam from sub-form fields
      const extParam = {};
      const senderAccount = {};
      if (values.sender_bankCode) senderAccount.bankCode = values.sender_bankCode;
      if (values.sender_bankAccountNo) senderAccount.bankAccountNo = values.sender_bankAccountNo;
      if (values.sender_bankAccountName) senderAccount.bankAccountName = values.sender_bankAccountName;
      if (values.sender_bankCountry) senderAccount.bankCountry = values.sender_bankCountry;
      if (Object.keys(senderAccount).length > 0) extParam.senderAccount = senderAccount;

      // beneficiary: id or account
      if (values.ext_beneficiaryId) {
        extParam.beneficiaryId = values.ext_beneficiaryId;
      } else {
        const beneficiaryAccount = {};
        if (values.ben_bankCode) beneficiaryAccount.bankCode = values.ben_bankCode;
        if (values.ben_bankAccountNo) beneficiaryAccount.bankAccountNo = values.ben_bankAccountNo;
        if (values.ben_bankAccountName) beneficiaryAccount.bankAccountName = values.ben_bankAccountName;
        if (values.ben_bankCountry) beneficiaryAccount.bankCountry = values.ben_bankCountry;
        if (values.ben_beneficiaryCity) beneficiaryAccount.beneficiaryCity = values.ben_beneficiaryCity;
        if (values.ben_beneficiaryTown) beneficiaryAccount.beneficiaryTown = values.ben_beneficiaryTown;
        if (values.ben_beneficiaryAddress1) beneficiaryAccount.beneficiaryAddress1 = values.ben_beneficiaryAddress1;
        if (values.ben_beneficiaryAddress2) beneficiaryAccount.beneficiaryAddress2 = values.ben_beneficiaryAddress2;
        if (Object.keys(beneficiaryAccount).length > 0) extParam.beneficiaryAccount = beneficiaryAccount;
      }

      // other ext fields
      if (values.ext_purpose) extParam.purpose = values.ext_purpose;
      if (values.ext_description) extParam.description = values.ext_description;
      if (values.ext_chargeType) extParam.chargeType = values.ext_chargeType;
      if (values.ext_transferMethod) extParam.transferMethod = values.ext_transferMethod;

      const requestBody = {
        requestId: 'req_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
        channelCode: values.channelCode,
        amount: values.amount,
        currency: values.currency,
        externalUserId: values.externalUserId,
        externalOrderId: values.externalOrderId,
        callbackUrl: values.callbackUrl || undefined,
        purpose: values.purpose || undefined,
        description: values.description || undefined,
        chargeType: values.chargeType || undefined,
        extParam: JSON.stringify(extParam),
      };

      const username = localStorage.getItem('username') || 'admin';
      const resp = await testApi.executeTest({
        testType: 'FUND_TRANSFER',
        channelCode: values.channelCode,
        businessPartner: values.businessPartner || 'test',
        currency: values.currency,
        env: 'SANDBOX',
        executor: username,
        requestBody: JSON.stringify(requestBody),
      });
      let res = resp.data;
      if (res.data && res.data.responseBody && typeof res.data.responseBody === 'string') {
        try { res.data.responseBody = JSON.parse(res.data.responseBody); } catch (e) { /* ignore */ }
      }
      setCreateResult(res);
      if (res.data && res.data.success) {
        message.success('调拨创建成功');
      } else {
        const errMsg = (res.data && res.data.errorMessage) || res.msg || '未知错误';
        message.warning('创建失败: ' + errMsg);
      }
    } catch (error) {
      const msg = (error.response && error.response.data && error.response.data.msg) || error.message;
      message.error('创建失败: ' + msg);
      setCreateResult((error.response && error.response.data) || { error: error.message });
    } finally {
      setLoading(false);
    }
  };

  // --- Tab 2: Query Fund Transfer ---
  const handleQuery = async (values) => {
    setQueryLoading(true);
    try {
      const requestBody = {
        requestId: 'req_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
        externalOrderId: values.externalOrderId || undefined,
        orderId: values.orderId || undefined,
        channelCode: values.channelCode || undefined,
      };
      const username = localStorage.getItem('username') || 'admin';
      const resp = await testApi.executeTest({
        testType: 'FUND_TRANSFER_QUERY',
        channelCode: values.channelCode || '',
        businessPartner: 'test',
        currency: '',
        env: 'SANDBOX',
        executor: username,
        requestBody: JSON.stringify(requestBody),
      });
      let res = resp.data;
      if (res.data && res.data.responseBody && typeof res.data.responseBody === 'string') {
        try { res.data.responseBody = JSON.parse(res.data.responseBody); } catch (e) { /* ignore */ }
      }
      setQueryResult(res);
    } catch (error) {
      const msg = (error.response && error.response.data && error.response.data.msg) || error.message;
      message.error('查询失败: ' + msg);
      setQueryResult((error.response && error.response.data) || { error: error.message });
    } finally {
      setQueryLoading(false);
    }
  };

  // --- Tab 3: Channel Transactions ---
  const handleTxnQuery = async (values) => {
    setTxnLoading(true);
    try {
      const requestBody = {
        requestId: 'req_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
        channelCode: values.channelCode,
        channelAccountNo: values.channelAccountNo,
        currency: values.currency,
        startDate: values.startDate,
        endDate: values.endDate,
        pageIndex: values.pageIndex || '1',
        pageSize: values.pageSize || '20',
      };
      const username = localStorage.getItem('username') || 'admin';
      const resp = await testApi.executeTest({
        testType: 'CHANNEL_TRANSACTIONS',
        channelCode: values.channelCode,
        businessPartner: 'test',
        currency: values.currency,
        env: 'SANDBOX',
        executor: username,
        requestBody: JSON.stringify(requestBody),
      });
      let res = resp.data;
      if (res.data && res.data.responseBody && typeof res.data.responseBody === 'string') {
        try { res.data.responseBody = JSON.parse(res.data.responseBody); } catch (e) { /* ignore */ }
      }
      setTxnResult(res);
    } catch (error) {
      const msg = (error.response && error.response.data && error.response.data.msg) || error.message;
      message.error('查询失败: ' + msg);
      setTxnResult((error.response && error.response.data) || { error: error.message });
    } finally {
      setTxnLoading(false);
    }
  };

  // --- Tab 1 Content ---
  const createTab = (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0', padding: 24 }}>
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreate}
          initialValues={{
            channelCode: 'BANKING_CIRCLE',
            transferMethod: 'BANK_TRANSFER',
            businessPartner: 'test',
            externalOrderId: 'FT_' + Date.now(),
            callbackUrl: 'https://merchant.com/callback',
          }}
        >
          <Form.Item name="businessPartner" hidden><Input /></Form.Item>

          <div style={sectionStyle}>基本信息</div>
          <div style={gridCol2}>
            <Form.Item label="渠道 (channelCode)" name="channelCode" rules={[{ required: true }]}>
              <Select options={CHANNEL_OPTIONS} />
            </Form.Item>
            <Form.Item label="金额 (amount)" name="amount" rules={[{ required: true }]}>
              <Input placeholder="100.00" />
            </Form.Item>
          </div>
          <div style={gridCol2}>
            <Form.Item label="币种 (currency)" name="currency" rules={[{ required: true }]}>
              <Input placeholder="USD" />
            </Form.Item>
            <Form.Item label="外部用户ID (externalUserId)" name="externalUserId" rules={[{ required: true }]}>
              <Input placeholder="user_001" style={{ fontFamily: 'monospace' }} />
            </Form.Item>
          </div>

          <div style={sectionStyleMt}>调拨参数</div>
          <div style={gridCol2}>
            <Form.Item label="外部订单号 (externalOrderId)" name="externalOrderId" rules={[{ required: true }]}>
              <Input style={{ fontFamily: 'monospace' }} />
            </Form.Item>
            <Form.Item label="回调地址 (callbackUrl)" name="callbackUrl">
              <Input placeholder="https://merchant.com/callback" />
            </Form.Item>
          </div>
          <div style={gridCol2}>
            <Form.Item label="用途 (purpose)" name="purpose">
              <Input placeholder="Transfer purpose" />
            </Form.Item>
            <Form.Item label="描述 (description)" name="description">
              <Input placeholder="Transfer description" />
            </Form.Item>
          </div>
          <Form.Item label="费用承担方 (chargeType)" name="chargeType" style={{ maxWidth: 300 }}>
            <Select options={CHARGE_TYPE_OPTIONS} allowClear placeholder="选择费用承担方" />
          </Form.Item>

          <Collapse
            style={{ marginBottom: 16 }}
            items={[
              {
                key: 'extParam',
                label: 'extParam 详细参数',
                children: (
                  <div>
                    <div style={sectionStyle}>发送方账户 (senderAccount)</div>
                    <div style={gridCol2}>
                      <Form.Item label="银行代码 (bankCode)" name="sender_bankCode" rules={[{ required: true }]} tooltip="sender bank code">
                        <Input placeholder="BANKCODE" style={{ fontFamily: 'monospace' }} />
                      </Form.Item>
                      <Form.Item label="银行账号 (bankAccountNo)" name="sender_bankAccountNo">
                        <Input placeholder="1234567890" style={{ fontFamily: 'monospace' }} />
                      </Form.Item>
                    </div>
                    <div style={gridCol2}>
                      <Form.Item label="账户名称 (bankAccountName)" name="sender_bankAccountName">
                        <Input placeholder="Account Holder Name" />
                      </Form.Item>
                      <Form.Item label="银行国家 (bankCountry)" name="sender_bankCountry" tooltip="ISO2">
                        <Input placeholder="US" maxLength={2} />
                      </Form.Item>
                    </div>

                    <div style={sectionStyleMt}>收款方 (二选一: beneficiaryId 或 beneficiaryAccount)</div>
                    <Form.Item label="收款人ID (beneficiaryId)" name="ext_beneficiaryId" tooltip="已有收款人ID，填此则忽略下方账户信息">
                      <Input placeholder="已有收款人ID" style={{ fontFamily: 'monospace' }} />
                    </Form.Item>
                    <div style={gridCol2}>
                      <Form.Item label="收款银行代码 (bankCode)" name="ben_bankCode">
                        <Input placeholder="BANKCODE" style={{ fontFamily: 'monospace' }} />
                      </Form.Item>
                      <Form.Item label="收款银行账号 (bankAccountNo)" name="ben_bankAccountNo">
                        <Input placeholder="9876543210" style={{ fontFamily: 'monospace' }} />
                      </Form.Item>
                    </div>
                    <div style={gridCol2}>
                      <Form.Item label="收款账户名 (bankAccountName)" name="ben_bankAccountName">
                        <Input placeholder="Beneficiary Name" />
                      </Form.Item>
                      <Form.Item label="收款银行国家 (bankCountry)" name="ben_bankCountry" tooltip="ISO2">
                        <Input placeholder="GB" maxLength={2} />
                      </Form.Item>
                    </div>
                    <div style={gridCol2}>
                      <Form.Item label="收款城市 (beneficiaryCity)" name="ben_beneficiaryCity">
                        <Input placeholder="London" />
                      </Form.Item>
                      <Form.Item label="收款镇 (beneficiaryTown)" name="ben_beneficiaryTown">
                        <Input placeholder="可选" />
                      </Form.Item>
                    </div>
                    <div style={gridCol2}>
                      <Form.Item label="收款地址1 (beneficiaryAddress1)" name="ben_beneficiaryAddress1">
                        <Input placeholder="123 Main St" />
                      </Form.Item>
                      <Form.Item label="收款地址2 (beneficiaryAddress2)" name="ben_beneficiaryAddress2">
                        <Input placeholder="Suite 100" />
                      </Form.Item>
                    </div>

                    <div style={sectionStyleMt}>其他</div>
                    <div style={gridCol2}>
                      <Form.Item label="用途 (purpose)" name="ext_purpose">
                        <Input placeholder="Purpose" />
                      </Form.Item>
                      <Form.Item label="描述 (description)" name="ext_description">
                        <Input placeholder="Description" />
                      </Form.Item>
                    </div>
                    <div style={gridCol2}>
                      <Form.Item label="费用承担方 (chargeType)" name="ext_chargeType">
                        <Select options={CHARGE_TYPE_OPTIONS} allowClear placeholder="选择" />
                      </Form.Item>
                      <Form.Item label="转账方式 (transferMethod)" name="ext_transferMethod">
                        <Input placeholder="BANK_TRANSFER" />
                      </Form.Item>
                    </div>
                  </div>
                ),
              },
            ]}
          />

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block style={{ height: 40 }}>
              提交调拨
            </Button>
          </Form.Item>
        </Form>
      </div>
      <div>
        <div style={sectionStyle}>创建结果</div>
        {renderResultPanel(createResult)}
      </div>
    </div>
  );

  // --- Tab 2 Content ---
  const queryTab = (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0', padding: 24 }}>
        <Form form={queryForm} layout="vertical" onFinish={handleQuery}>
          <div style={sectionStyle}>查询条件</div>
          <Form.Item label="外部订单号 (externalOrderId)" name="externalOrderId">
            <Input placeholder="FT_xxxxx" style={{ fontFamily: 'monospace' }} />
          </Form.Item>
          <Form.Item label="平台订单号 (orderId)" name="orderId">
            <Input placeholder="平台内部订单号" style={{ fontFamily: 'monospace' }} />
          </Form.Item>
          <Form.Item label="渠道 (channelCode)" name="channelCode">
            <Select options={CHANNEL_OPTIONS} allowClear placeholder="选择渠道" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={queryLoading} block style={{ height: 40 }}>
              查询调拨
            </Button>
          </Form.Item>
        </Form>
      </div>
      <div>
        <div style={sectionStyle}>查询结果</div>
        {renderResultPanel(queryResult)}
      </div>
    </div>
  );

  // --- Tab 3 Content ---
  const txnTab = (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0', padding: 24 }}>
        <Form
          form={txnForm}
          layout="vertical"
          onFinish={handleTxnQuery}
          initialValues={{ pageIndex: '1', pageSize: '20' }}
        >
          <div style={sectionStyle}>查询条件</div>
          <div style={gridCol2}>
            <Form.Item label="渠道 (channelCode)" name="channelCode" rules={[{ required: true }]}>
              <Select options={CHANNEL_OPTIONS} placeholder="选择渠道" />
            </Form.Item>
            <Form.Item label="渠道账号 (channelAccountNo)" name="channelAccountNo" rules={[{ required: true }]}>
              <Input placeholder="渠道账号" style={{ fontFamily: 'monospace' }} />
            </Form.Item>
          </div>
          <div style={gridCol2}>
            <Form.Item label="币种 (currency)" name="currency" rules={[{ required: true }]}>
              <Input placeholder="USD" />
            </Form.Item>
            <Form.Item label="开始日期 (startDate)" name="startDate" rules={[{ required: true }]}>
              <Input placeholder="yyyyMMdd e.g. 20260801" />
            </Form.Item>
          </div>
          <div style={gridCol2}>
            <Form.Item label="结束日期 (endDate)" name="endDate" rules={[{ required: true }]}>
              <Input placeholder="yyyyMMdd e.g. 20260814" />
            </Form.Item>
            <Form.Item label="页码 (pageIndex)" name="pageIndex">
              <Input placeholder="1" />
            </Form.Item>
          </div>
          <Form.Item label="每页条数 (pageSize)" name="pageSize" style={{ maxWidth: 300 }}>
            <Input placeholder="20" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={txnLoading} block style={{ height: 40 }}>
              查询流水
            </Button>
          </Form.Item>
        </Form>
      </div>
      <div>
        <div style={sectionStyle}>查询结果</div>
        {renderResultPanel(txnResult)}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <Tabs
        defaultActiveKey="create"
        items={[
          { key: 'create', label: '创建调拨', children: createTab },
          { key: 'query', label: '查询调拨', children: queryTab },
          { key: 'transactions', label: '渠道流水查询', children: txnTab },
        ]}
      />
    </div>
  );
}
